/**
 * SettingsSidebar — the two-pane sidebar shown on `/settings/*` routes.
 *
 * This replaces (does NOT extend) the main [`AppSidebar`](client/src/components/AppSidebar.tsx:1)
 * while the user is in Settings. The user clicks the settings cog in the
 * main sidebar to enter, and the "← Back" button at the top of this sidebar
 * to leave.
 *
 * Layout (matches `ianfiles/sidebar-layout.md`):
 *
 *   ┌──────────────┬──────────────────────────┐
 *   │ ← Back       │  Settings                │
 *   ├──────────────┼──────────────────────────┤
 *   │ 👤 Account   │  Profile                 │
 *   │ 🏢 My Co. *  │  Team                    │
 *   │              │  Organization            │
 *   │ WORKSPACE    │                          │
 *   │ 🏭 Operations│  (items for the          │
 *   │ ...          │   currently-selected     │
 *   └──────────────┴──────────────────────────┘
 *      * direct-link categories hide the items column,
 *        so the sidebar collapses back to just the rail.
 *
 * The component is mounted inside the same shadcn `SidebarProvider` as the
 * main app sidebar, so `<SidebarTrigger>` in the header still toggles the
 * whole nav surface (⌘B / Ctrl+B).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { Sidebar, useSidebar } from "@/components/ui/sidebar";
import {
  SETTINGS_SECTION_LABELS,
  type SettingsCategory,
  type SettingsSection,
  findCategoryForPath,
  visibleCategories,
} from "@/lib/settings-routes";

const RAIL_WIDTH_PX = "14rem";
const ITEMS_WIDTH_PX = "16rem";

export function SettingsSidebar() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { setOpenMobile } = useSidebar();

  const categories = useMemo(() => visibleCategories(user?.role), [user?.role]);

  // The active category is derived from the current URL when possible. If the
  // URL doesn't match any item (e.g. the user is on /settings root), we fall
  // back to the first visible category so the items column always has content.
  const matched = useMemo(
    () => findCategoryForPath(location, user?.role),
    [location, user?.role]
  );

  const [selectedId, setSelectedId] = useState<string>(
    () => matched?.id ?? categories[0]?.id ?? "account"
  );

  // Track the last URL-driven match so we can distinguish "URL changed under
  // me" from "the user clicked a category in the rail". Without this guard,
  // the effect below would clobber user clicks on multi-item categories
  // (which deliberately don't navigate) and snap the items column back to
  // whatever the URL implies — causing a flicker.
  const lastMatchedIdRef = useRef<string | undefined>(matched?.id);

  useEffect(() => {
    const newMatchedId = matched?.id;
    if (newMatchedId !== lastMatchedIdRef.current) {
      lastMatchedIdRef.current = newMatchedId;
      if (newMatchedId) {
        setSelectedId(newMatchedId);
      }
    }
  }, [matched]);

  const selected =
    categories.find((c) => c.id === selectedId) ?? categories[0];

  const showItemsColumn = Boolean(selected && !selected.directUrl);

  // Group categories by section, preserving section order from
  // SETTINGS_SECTION_LABELS.
  const grouped = useMemo(() => {
    const order: SettingsSection[] = [
      "account",
      "workspace",
      "sales",
      "integrations",
      "admin",
    ];
    return order
      .map((section) => ({
        section,
        label: SETTINGS_SECTION_LABELS[section],
        categories: categories.filter((c) => c.section === section),
      }))
      .filter((g) => g.categories.length > 0);
  }, [categories]);

  return (
    <Sidebar>
      <div className="flex h-full">
        {/* Categories rail */}
        <aside
          className="flex shrink-0 flex-col border-r"
          style={{ width: RAIL_WIDTH_PX }}
        >
          <div className="flex h-14 items-center gap-2 border-b px-3">
            <button
              type="button"
              onClick={() => {
                setLocation("/");
                setOpenMobile(false);
              }}
              className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground hover-elevate active-elevate-2"
              aria-label="Back to app"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
          </div>

          <nav className="flex-1 overflow-auto py-2">
            {grouped.map((group, idx) => (
              <div key={group.section} className={cn(idx > 0 && "mt-3")}>
                {group.label && (
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </div>
                )}
                <ul className="space-y-0.5 px-2">
                  {group.categories.map((cat) => (
                    <li key={cat.id}>
                      <CategoryButton
                        category={cat}
                        isSelected={cat.id === selectedId}
                        onSelect={() => {
                          setSelectedId(cat.id);
                          if (cat.directUrl) {
                            setLocation(cat.directUrl);
                            setOpenMobile(false);
                          }
                          // For categories with items we deliberately do NOT
                          // navigate — the items column expands and the user
                          // picks one. This avoids surprise navigations when
                          // exploring categories.
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Items column — hidden when the selected category is a direct link.
            Uses the same `bg-sidebar` surface as the rail so the two panes
            read as a single sidebar, with a right border to clearly separate
            the whole sidebar from the page content. */}
        {showItemsColumn && selected && (
          <section
            className="flex shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground"
            style={{ width: ITEMS_WIDTH_PX }}
          >
            <div className="flex h-14 items-center border-b px-4">
              <h2 className="text-base font-semibold">{selected.title}</h2>
            </div>
            <nav className="flex-1 overflow-auto p-2">
              <ul className="space-y-0.5">
                {selected.items?.map((item) => {
                  const isActive =
                    location === item.url || location.startsWith(item.url + "/");
                  return (
                    <li key={item.id}>
                      <Link
                        href={item.url}
                        onClick={() => setOpenMobile(false)}
                        className={cn(
                          "flex items-center rounded-md px-3 py-2 text-sm transition-colors",
                          isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                            : "text-foreground/80 hover-elevate active-elevate-2"
                        )}
                      >
                        {item.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </section>
        )}
      </div>
    </Sidebar>
  );
}

function CategoryButton({
  category,
  isSelected,
  onSelect,
}: {
  category: SettingsCategory;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const Icon = category.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
        isSelected
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
          : "text-foreground/80 hover-elevate active-elevate-2"
      )}
      aria-current={isSelected ? "page" : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{category.title}</span>
    </button>
  );
}
